'use client';


import ImageProxy from '@next-image-proxy';

import { DollarCoin } from '@uikit/icons/DollarCoin';

import useDisableRedirectOnLogin from '@shared/hooks/useDisableRedirectOnLogin';

import CustomStoryBanner from '../CustomStoryBanner';
import CustomStoryLayout from '../CustomStoryLayout';
import CustomStoryParagraph from '../CustomStoryParagraph';
import CustomStorySections from '../CustomStorySections';
import CustomStoryStatCard from '../CustomStoryStatCard';
import AutoLayerStoryLeaderboard from './AutoLayerStoryLeaderboard';
import AutoLayerStoryRewardTable from './AutoLayerStoryRewardTable';
import { investors, rewards, roadToIdoStat, stats } from './data';
import StepToJoinAutoLayerCampaign from './StepToJoinAutoLayerCampaign';

const sections = [
  {
    id: 'overview',
    title: 'Project Overview',
    content: (
      <>
        <CustomStoryParagraph className="mb-5 md:mb-10 md:text-2xl font-light">
          Discover the future of decentralized finance with AutoLayer, your ultimate Liquid
          Restaking Tokens (LRT) finance application. Effortlessly restake a variety of tokens with
          just one click, optimizing your yields while maintaining simplicity and efficiency. As the
          definitive gateway to LRTfi, AutoLayer empowers you to craft personalized strategies that
          maximize the potential of LRTs. In its innovative integration within the EigenLayer
          ecosystem, AutoLayer stands out by managing not just LRTs but also Liquid Staked Tokens
          (LST), AVSs, and more. Whether you&apos;re looking to generate unprecedented yields
          through intuitive LRTfi automations or seeking top-tier security measures in DeFi,
          AutoLayer is designed to enhance your investment experience with advanced analytics,
          secure protocols, and seamless functionality. Start with any asset and transform it into
          substantial returns, all within a few clicks in AutoLayer&apos;s sophisticated interface.
        </CustomStoryParagraph>

        <ImageProxy
          alt="Auto Layer. One Click to DeFi Mastery. Harness the Power of Liquid Restaking with AutoLayer."
          className="w-full h-auto mb-4 rounded-2xl"
          width={1160}
          height={533}
          quality={80}
          src="/launchpad/stories/autolayer/auto-layer-story-overview-1.png"
        />

        <ImageProxy
          alt="Auto Layer. Secure, Smart, and Seamless. Elevate Your DeFi Strategy with AutoLayer."
          className="w-full h-auto rounded-2xl"
          width={1160}
          height={533}
          quality={80}
          src="/launchpad/stories/autolayer/auto-layer-story-overview-2.png"
        />
      </>
    ),
  },
  {
    id: 'investors',
    title: 'AutoLayer Investors',
    content: (
      <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {investors.map(({ image, name }, i) => {
          return (
            <li
              className="flex items-center rounded-lg bg-primary-100 w-full px-[1.125rem] justify-center h-20"
              key={i}
            >
              <ImageProxy
                className="w-full h-full max-w-[15.375rem] object-contain"
                alt={name}
                quality={80}
                {...image}
              />
            </li>
          );
        })}
      </ul>
    ),
  },
  {
    id: 'about',
    title: 'About the Campaign',
    content: (
      <>
        <p className="md:text-2xl font-light">
          AutoLayer offers a generous reward pool of $200,000 in $LAY3R Tokens for its Road-to-IDO
          Campaign, powered by Magic Square. Participants in this campaign not only have the
          opportunity to win a share of the prize pool but are also guaranteed whitelisting for
          further allocations in the upcoming AutoLayer IDO on the Magic Launchpad. Detailed
          information on participation will be provided soon.
          <br />
          Your engagement and activity levels can significantly boost your chances to earn more
          $LAY3R tokens! Magic Square is thrilled to host this campaign, providing full support to
          all participants and ensuring a seamless experience. Join us on this exciting journey
          towards the AutoLayer IDO and seize your chance to be part of something big!
        </p>
      </>
    ),
  },
  {
    id: 'rewards',
    title: 'Campaign Rewards',
    content: (
      <>
        <ul className="flex max-md:flex-col gap-4 md:gap-6 mb-5 md:mb-10">
          {['$200,000 in Airdrop Rewards', 'Additional $100,000 in IDO Allocations'].map(
            (item, i) => {
              return (
                <li
                  key={i}
                  className="flex flex-1 text-center text-secondary-600 font-semibold md:text-2xl items-center justify-center p-6 bg-[#D7DDFF] rounded-2xl"
                >
                  {item}
                </li>
              );
            },
          )}
        </ul>

        <div className="flex flex-col gap-7 md:gap-[3.25rem]">
          <ul className="flex flex-wrap gap-7 md:gap-[3.25rem]">
            {stats.map(({ name, description, Icon, stat }, i) => {
              return (
                <li key={i} className="flex-grow">
                  <CustomStoryStatCard
                    title={name}
                    description={description}
                    stat={stat}
                    icon={<Icon />}
                  />
                </li>
              );
            })}
          </ul>

          <AutoLayerStoryRewardTable data={rewards} />

          <p className="font-semibold text-secondary-600 md:text-[2rem] md:leading-[2.375rem] text-center p-6 bg-[#D7DDFF] rounded-2xl">
            Raffle winners will have access to an IDO pool valued at $100,000 in $LAY3R Tokens. Any
            unsold allocations from this pool will be transferred to the Public FCFS Pool, open to
            everyone.
          </p>

          <CustomStoryStatCard
            title={roadToIdoStat.name}
            stat={roadToIdoStat.stat}
            icon={<DollarCoin />}
          />
        </div>
      </>
    ),
  },
  {
    id: 'join-campaign',
    title: 'Steps to Join the Campaign',
    content: <StepToJoinAutoLayerCampaign />,
  },
  {
    title: 'Leaderboard',
    id: 'leaderboard',
    content: (
      <>
        <AutoLayerStoryLeaderboard />
        <p className="max-md:text-xs text-neutral-500 mt-2.5">
          The leaderboard updates every 10 minutes, combining points from tasks on the Zealy
          Campaign page and your AutoLayer activity. Your overall rank is determined by the total
          points from both sources. To improve your ranking, stay active on both AutoLayer and the
          Zealy page.
          <br />
          <br />
          Please note that only points accumulated on AutoLayer from the beginning of this campaign
          will be considered towards your total. Points earned prior to the start of this campaign
          will not be included.
          <br />
          <br />
          Additionally, to appear on this leaderboard, you MUST have at least 1 point on Zealy.
          While it is not mandatory to have points on AutoLayer, doing so will significantly enhance
          your chances of winning token rewards and IDO whitelist spots.
        </p>
      </>
    ),
  },
];

const AutoLayerStory = () => {
  useDisableRedirectOnLogin();

  return (
    <CustomStoryLayout
      title="AutoLayer Project Overview and Campaign Details"
      banner={
        <CustomStoryBanner
          alt="Magic Launchpad Presents AutoLayer Road-to-IDO $200,000 in $LAY3R Prize Pool + $100,000 IDO Whitelist"
          desktop={{
            src: '/launchpad/stories/autolayer/auto-layer-story-banner-desktop.png',
            width: 1300,
            height: 225,
            quality: 80,
          }}
          tablet={{
            src: '/launchpad/stories/autolayer/auto-layer-story-banner-tablet.png',
            width: 1300,
            height: 570,
            quality: 80,
          }}
          mobile={{
            src: '/launchpad/stories/autolayer/auto-layer-story-banner-mobile.png',
            width: 1500,
            height: 570,
            quality: 80,
          }}
        />
      }
    >
      <p className="font-semibold text-secondary-600 md:text-[2rem] md:leading-[2.375rem] text-center p-6 bg-[#D7DDFF] rounded-2xl mb-5 md:mb-10">
        Join the AutoLayer campaign and secure your $LAY3R tokens and IDO allocation from the
        leading Liquid Restaked Tokens (LRT) protocol in crypto today.
      </p>

      <CustomStorySections sections={sections} />
    </CustomStoryLayout>
  );
};

export default AutoLayerStory;
